import { SESSION_STATUS } from "./session-status.js";

function asStr(v) {
  return String(v ?? "").trim();
}

/** Feste System-IDs für Termin-Absagen (Statuswerte in trainerstunden_sessions). */
export const BUILTIN_SESSION_CANCEL_REASON_IDS = Object.freeze([
  SESSION_STATUS.CANCELLED_WEATHER,
  SESSION_STATUS.CANCELLED_TRAINER,
  SESSION_STATUS.CANCELLED_COURT,
  SESSION_STATUS.CANCELLED_PARTICIPANTS,
  SESSION_STATUS.CANCELLED_CLUB,
  SESSION_STATUS.CANCELLED_HOLIDAY,
  SESSION_STATUS.POSTPONED,
  SESSION_STATUS.CANCELLED_OTHER,
]);

/**
 * @returns {import('./tenant-reference-lists.js').SessionCancelReasonEntry[]}
 */
export function defaultSessionCancelReasonEntries() {
  return BUILTIN_SESSION_CANCEL_REASON_IDS.map((id, index) => ({
    id,
    builtin: true,
    enabled: true,
    sort: (index + 1) * 10,
  }));
}

/**
 * @param {unknown} lists
 * @returns {import('./tenant-reference-lists.js').SessionCancelReasonEntry[]}
 */
export function resolveSessionCancelReasonEntries(lists) {
  const configured = lists && typeof lists === "object" ? lists.sessionCancelReasons : null;
  if (!Array.isArray(configured) || !configured.length) {
    return defaultSessionCancelReasonEntries();
  }

  const defaultsById = new Map(defaultSessionCancelReasonEntries().map((row) => [row.id, row]));
  const out = [];
  const seen = new Set();

  for (const raw of configured) {
    if (!raw || typeof raw !== "object") continue;
    const id = asStr(raw.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const fallback = defaultsById.get(id);
    out.push({
      id,
      builtin: raw.builtin === true || Boolean(fallback),
      enabled: raw.enabled !== false,
      sort: Number(raw.sort) || fallback?.sort || (out.length + 1) * 10,
      label: asStr(raw.label) || undefined,
    });
  }

  for (const fallback of defaultSessionCancelReasonEntries()) {
    if (!seen.has(fallback.id)) out.push({ ...fallback });
  }

  return out
    .filter((row) => row.enabled !== false)
    .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || asStr(a.id).localeCompare(asStr(b.id)));
}

/**
 * Alle konfigurierten Einträge inkl. deaktivierter (für Admin-UI).
 *
 * @param {unknown} lists
 */
export function resolveSessionCancelReasonEntriesForAdmin(lists) {
  const configured = lists && typeof lists === "object" ? lists.sessionCancelReasons : null;
  if (!Array.isArray(configured) || !configured.length) {
    return defaultSessionCancelReasonEntries();
  }

  const defaultsById = new Map(defaultSessionCancelReasonEntries().map((row) => [row.id, row]));
  const out = [];
  const seen = new Set();

  for (const raw of configured) {
    if (!raw || typeof raw !== "object") continue;
    const id = asStr(raw.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const fallback = defaultsById.get(id);
    out.push({
      id,
      builtin: raw.builtin === true || Boolean(fallback),
      enabled: raw.enabled !== false,
      sort: Number(raw.sort) || fallback?.sort || (out.length + 1) * 10,
      label: asStr(raw.label) || undefined,
    });
  }

  for (const fallback of defaultSessionCancelReasonEntries()) {
    if (!seen.has(fallback.id)) out.push({ ...fallback });
  }

  return out.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || asStr(a.id).localeCompare(asStr(b.id)));
}

/**
 * @param {{ id: string, label?: string, builtin?: boolean }} entry
 * @param {(key: string) => string} t
 */
export function resolveSessionCancelReasonLabel(entry, t) {
  const custom = asStr(entry?.label);
  if (custom) return custom;
  const id = asStr(entry?.id);
  if (!id) return "";
  return typeof t === "function" ? t(`trainerstunden.status.${id}`) : id;
}

/**
 * @param {unknown} lists
 * @returns {string[]}
 */
export function resolveSessionCancelReasonIds(lists) {
  return resolveSessionCancelReasonEntries(lists).map((row) => row.id);
}

/**
 * @param {string} reasonId
 * @param {unknown} lists
 */
export function isAllowedSessionCancelReasonId(reasonId, lists) {
  const id = asStr(reasonId);
  if (!id) return false;
  return resolveSessionCancelReasonIds(lists).includes(id);
}
